import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CommonLayout from '../../layouts/CommonLayout';
import { getMeetingApplication, deleteMeetingApplication } from '../../../api/MeetingApplicationApi';
import useLoadingStore from '../../../store/loadingStore';
import useAuthStore from '../../../store/authStore';
import { IoDocumentTextOutline, IoRestaurantOutline } from 'react-icons/io5';
import { AlertModal } from '../../layouts/AlertModal';
import { ConfirmModal } from '../../layouts/ConfirmModal';

const MeetingApplicationPage = () => {
    const { meetingApplicationId } = useParams();
    const [applicationData, setApplicationData] = useState(null);
    const { setIsLoading } = useLoadingStore();
    const { member } = useAuthStore();
    const navigate = useNavigate();

    // 모달 상태 관리
    const [alertState, setAlertState] = useState({ isVisible: false, message: '', callback: null });
    const [confirmState, setConfirmState] = useState({ isVisible: false, title: '', message: '', onConfirm: () => {} });

    // 헬퍼 함수
    const showConfirm = (title, message, onConfirmAction) => {
        setConfirmState({
            isVisible: true,
            title,
            message,
            onConfirm: () => {
                onConfirmAction();
                setConfirmState({ ...confirmState, isVisible: false });
            }
        });
    };

    const getErrorMessage = (error) => {
        if (error.response && error.response.data && error.response.data.message) {
            return error.response.data.message;
        }
        return '오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
    };

    useEffect(() => {
        const loadApplicationData = async () => {
            try {
                setIsLoading(true);
                const response = await getMeetingApplication(meetingApplicationId);
                setApplicationData(response);
            } catch (error) {
                console.error('신청서 조회 실패:', error);
                setAlertState({ isVisible: true, message: '신청서를 불러오는 중 오류가 발생했습니다.' });
            } finally {
                setIsLoading(false);
            }
        };
        loadApplicationData();
    }, [meetingApplicationId, setIsLoading]);

    const handleCancelApplication = async () => {
        showConfirm('모임 참가 신청 취소', '정말로 모임 참가 신청을 취소하시겠습니까?\n쌀 500알이 환급됩니다.', async () => {
            try {
                setIsLoading(true);
                await deleteMeetingApplication(applicationData.id);
                setAlertState({
                    isVisible: true,
                    message: '모임 참가 신청이 취소되었습니다.',
                    callback: () => navigate('/app/mypage')
                });
            } catch (error) {
                console.error('모임 참가 신청 취소 실패:', error);
                setAlertState({ isVisible: true, message: getErrorMessage(error) });
            } finally {
                setIsLoading(false);
            }
        });
    };

    if (!applicationData) {
        return (
            <CommonLayout title="모임 참가 신청서">
                <AlertModal
                    isVisible={alertState.isVisible}
                    message={alertState.message}
                    callback={alertState.callback}
                    onClose={() => setAlertState({ isVisible: false, message: '', callback: null })}
                />
                <div className="flex items-center justify-center h-64">
                    <p className="text-gray-500">신청서를 불러오는 중...</p>
                </div>
            </CommonLayout>
        );
    }

    const isOwner = member?.id === applicationData.memberId;

    return (
        <CommonLayout title="모임 참가 신청서">
            <AlertModal
                isVisible={alertState.isVisible}
                message={alertState.message}
                callback={alertState.callback}
                onClose={() => setAlertState({ isVisible: false, message: '', callback: null })}
            />
            <ConfirmModal
                isVisible={confirmState.isVisible}
                title={confirmState.title}
                message={confirmState.message}
                onConfirm={confirmState.onConfirm}
                onCancel={() => setConfirmState({ ...confirmState, isVisible: false })}
            />
            <div className="max-w-lg mx-auto relative">
                <div className="p-4 space-y-6 pb-24">
                    {/* 모임 정보 */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center mb-3">
                            <IoRestaurantOutline size={20} className="text-amber-500 mr-2" />
                            <h2 className="text-md font-semibold text-gray-900">모임 정보</h2>
                        </div>
                        <h3 className="text-sm font-medium text-gray-800">
                            {applicationData.meetingTitle}
                        </h3>
                    </div>

                    {/* 소개글 */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center mb-3">
                            <IoDocumentTextOutline size={20} className="text-green-500 mr-2" />
                            <h2 className="text-md font-semibold text-gray-900">소개글</h2>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                                {applicationData.content}
                            </p>
                        </div>
                    </div>

                    {/* 신청 날짜 */}
                    <div className="text-center text-sm text-gray-500">
                        신청일: {new Date(applicationData.createdAt).toLocaleString('ko-KR')}
                    </div>
                </div>

                {/* 취소 버튼 */}
                {isOwner && (
                    <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200">
                        <div className="max-w-lg mx-auto">
                            <button
                                onClick={handleCancelApplication}
                                className="w-full py-3 bg-red-500 text-white rounded-lg font-medium active:bg-red-600 transition-colors"
                            >
                                모임 참가 신청 취소
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </CommonLayout>
    );
};

export default MeetingApplicationPage;
